import { useMemo } from 'react';
import { useContent } from 'src/contexts/ContentContext';
import { useUserHasFeature } from 'src/hooks/useUserHasFeature';
import { ContentId } from 'src/utils/constants/contentId';
import { Feature, ListingExclusivityClass } from 'src/WebApiController';

// POS-9467: most restrictive first, so the message reads "fan and broker" / "broker and non-b2b".
const classOrder = [
  ListingExclusivityClass.Fan,
  ListingExclusivityClass.Broker,
  ListingExclusivityClass.NonB2b,
];

/**
 * POS-9467: names the exclusivity classes a merge mixes, e.g. "You are merging fan and broker
 * inventory". Returns undefined when the merge is homogeneous, so the caller renders nothing.
 */
export const useMergeExclusivityWarningMessage = (
  sourceClasses: ListingExclusivityClass[] | undefined
) => {
  const fanBrokerMsg = useContent(
    ContentId.MergeListings_ExclusivityWarningFanBroker
  );
  const fanNonB2bMsg = useContent(
    ContentId.MergeListings_ExclusivityWarningFanNonB2b
  );
  const brokerNonB2bMsg = useContent(
    ContentId.MergeListings_ExclusivityWarningBrokerNonB2b
  );
  const fanBrokerNonB2bMsg = useContent(
    ContentId.MergeListings_ExclusivityWarningFanBrokerNonB2b
  );
  const hasMergeExclusivityWarningFeature = useUserHasFeature(
    Feature.ListingMergeExclusivityWarning
  );

  return useMemo(() => {
    if (!hasMergeExclusivityWarningFeature) return undefined;

    const presentClasses = classOrder.filter((c) => sourceClasses?.includes(c));
    if (presentClasses.length < 2) return undefined;

    switch (presentClasses.join(',')) {
      case 'Fan,Broker':
        return fanBrokerMsg;
      case 'Fan,NonB2b':
        return fanNonB2bMsg;
      case 'Broker,NonB2b':
        return brokerNonB2bMsg;
      default:
        return fanBrokerNonB2bMsg;
    }
  }, [
    sourceClasses,
    hasMergeExclusivityWarningFeature,
    fanBrokerMsg,
    fanNonB2bMsg,
    brokerNonB2bMsg,
    fanBrokerNonB2bMsg,
  ]);
};
